import { useEffect, useId, useRef, useState, type ChangeEvent, type DragEvent } from "react";

import {
  APIError,
  MAX_DOCUMENT_IMPORT_BYTES,
  createDocumentImport,
  getDocumentIngestion,
  type DocumentImportRecord,
  type DocumentIngestionStatus,
} from "./api.ts";
import {
  MAX_RESUME_IMPORT_BYTES,
  ResumeImportError,
  parseResumeImportJSON,
  resumeImportAIInstructions,
  resumeImportTemplateJSON,
  type ResumeImportResult,
} from "./resume-import.ts";
import "./import-center.css";

interface ImportCenterProps {
  open: boolean;
  onClose: () => void;
  onApply: (result: ResumeImportResult) => boolean;
}

export function ImportCenter({ open, onClose, onApply }: ImportCenterProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const sourceRef = useRef<HTMLTextAreaElement>(null);
  const inputID = useId().replace(/:/g, "");
  const documentInputID = `${inputID}-document`;
  const [source, setSource] = useState("");
  const [filename, setFilename] = useState("");
  const [result, setResult] = useState<ResumeImportResult | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentImport, setDocumentImport] = useState<DocumentImportRecord | null>(null);
  const [documentStatus, setDocumentStatus] = useState<DocumentIngestionStatus | null>(null);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [documentIssue, setDocumentIssue] = useState("");
  const [documentDragActive, setDocumentDragActive] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setSource("");
      setFilename("");
      setResult(null);
      setIssues([]);
      setNotice("");
      dialog.showModal();
      window.requestAnimationFrame(() => sourceRef.current?.focus());
      setDocumentFile(null);
      setDocumentImport(null);
      setDocumentStatus(null);
      setDocumentBusy(false);
      setDocumentIssue("");
      setDocumentDragActive(false);
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  function updateSource(value: string, nextFilename = "") {
    setSource(value);
  useEffect(() => {
    if (!open || !documentImport) return;
    let active = true;
    let timer: number | undefined;
    const controller = new AbortController();
    async function refresh() {
      try {
        const next = await getDocumentIngestion(documentImport!.document_id, controller.signal);
        if (!active) return;
        setDocumentStatus(next);
        if (documentImportTerminal(next) && timer !== undefined) window.clearInterval(timer);
      } catch (error) {
        if (!active || controller.signal.aborted) return;
        setDocumentIssue(error instanceof APIError ? `${error.code}: ${error.detail}` : "Zeitpoint could not refresh the conversion status.");
        if (timer !== undefined) window.clearInterval(timer);
      }
    }
    void refresh();
    timer = window.setInterval(() => void refresh(), 1500);
    return () => {
      active = false;
      controller.abort();
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [open, documentImport]);

    setFilename(nextFilename);
    setResult(null);
    setIssues([]);
    setNotice("");
  }

  function validateSource(value = source) {
    if (!value.trim()) {
      setResult(null);
      setIssues(["Choose a JSON file or paste JSON before validating."]);
      return;
    }
    try {
      const next = parseResumeImportJSON(value);
      setResult(next);
      setIssues([]);
      setNotice(next.warnings.length > 0
        ? `Structure accepted · ${next.warnings.length} format ${next.warnings.length === 1 ? "warning" : "warnings"} to review`
        : "Structure accepted · ready to import");
    } catch (error) {
      setResult(null);
      setNotice("");
      setIssues(error instanceof ResumeImportError ? error.issues : ["Zeitpoint could not read this JSON file."]);
    }
  }

  async function loadFile(file: File) {
    setDragActive(false);
    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      setResult(null);
      setIssues(["Choose a .json file. DOCX and PDF use the separate document conversion route."]);
      return;
    }
    if (file.size > MAX_RESUME_IMPORT_BYTES) {
      setResult(null);
      setIssues(["The JSON file is larger than 512 KB. Remove unrelated or embedded content and try again."]);
      return;
    }
    try {
      const text = await file.text();
      updateSource(text, file.name);
      validateSource(text);
    } catch {
      setResult(null);
      setIssues(["Zeitpoint could not read this file. Save it as UTF-8 JSON and try again."]);
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void loadFile(file);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) void loadFile(file);
  }

  function download(name: string, content: string, type: string) {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function copyInstructions() {
    try {
      await navigator.clipboard.writeText(resumeImportAIInstructions());
      setNotice("AI instructions copied · add your resume information after the template");
    } catch {
      download("zeitpoint-ai-import-instructions.txt", resumeImportAIInstructions(), "text/plain;charset=utf-8");
      setNotice("Clipboard access was unavailable · instructions downloaded instead");
    }
  }

  function applyImport() {
    if (!result) return;
    if (onApply(result)) onClose();
  }

  function selectDocumentFile(file: File) {
    setDocumentDragActive(false);
    setDocumentImport(null);
    setDocumentStatus(null);
    setDocumentIssue("");
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith(".docx") && !lowerName.endsWith(".pdf")) {
      setDocumentFile(null);
      setDocumentIssue("Choose one .docx or .pdf file. JSON uses the structured import area above.");
      return;
    }
    if (file.size < 1) {
      setDocumentFile(null);
      setDocumentIssue("The selected document is empty.");
      return;
    }
    if (file.size > MAX_DOCUMENT_IMPORT_BYTES) {
      setDocumentFile(null);
      setDocumentIssue("The selected document is larger than 25 MiB.");
      return;
    }
    setDocumentFile(file);
  }

  function handleDocumentFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) selectDocumentFile(file);
  }

  function handleDocumentDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) selectDocumentFile(file);
  }

  async function uploadDocument() {
    if (!documentFile || documentBusy) return;
    setDocumentBusy(true);
    setDocumentIssue("");
    setDocumentImport(null);
    setDocumentStatus(null);
    try {
      const created = await createDocumentImport(documentFile);
      setDocumentImport(created);
      setNotice("Document admitted · Zeitpoint is checking, parsing and mapping it into Generic BEP");
    } catch (error) {
      setDocumentIssue(error instanceof APIError ? `${error.code}: ${error.detail}` : "Zeitpoint could not upload this document.");
    } finally {
      setDocumentBusy(false);
    }
  }

  return <dialog
    ref={dialogRef}
    className="import-center"
    aria-labelledby="import-center-title"
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
  >
    <header className="import-center-header">
      <div><span aria-hidden="true">IN</span><div><h2 id="import-center-title">Import resume</h2><p>Structured JSON intake for a new BEP draft</p></div></div>
      <button type="button" className="import-close" onClick={onClose} aria-label="Close import center">Close</button>
    </header>

    <div className="import-center-body">
      <div className="import-workflow">
        <section className="import-step" aria-labelledby="import-source-title">
          <header><span aria-hidden="true">01</span><div><h3 id="import-source-title">Prepare JSON</h3><p>Use the exact template so every value has one predictable destination.</p></div></header>
          <div className="import-helper-actions">
            <button type="button" className="button button--quiet" onClick={() => download("zeitpoint-resume-import-v1.json", resumeImportTemplateJSON(), "application/json")}>Download template</button>
            <button type="button" className="button button--quiet" onClick={() => void copyInstructions()}>Copy AI instructions</button>
          </div>
          <p className="import-privacy">The AI step happens outside Zeitpoint. Review the provider's privacy terms before sharing personal information.</p>

          <div
            className={dragActive ? "import-dropzone is-active" : "import-dropzone"}
            onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (event.currentTarget === event.target) setDragActive(false); }}
            onDrop={handleDrop}
          >
            <input id={inputID} className="import-file-input" type="file" accept="application/json,.json" onChange={handleFileChange} />
            <label className="button button--primary" htmlFor={inputID}>Choose JSON file</label>
            <span>{filename || "or drop one .json file here · maximum 512 KB"}</span>
          </div>

          <label className="import-source" htmlFor={`${inputID}-source`}>
            <span>Paste JSON</span>
            <textarea ref={sourceRef} id={`${inputID}-source`} value={source} onChange={(event) => updateSource(event.target.value)} spellCheck={false} placeholder={'{\n  "format": "zeitpoint.resume-import",\n  "version": 1,\n  "resume": { ... }\n}'} />
          </label>
          <button type="button" className="button button--quiet import-validate" onClick={() => validateSource()}>Validate JSON</button>
        </section>

        <section className="import-step import-review" aria-labelledby="import-review-title">
          <header><span aria-hidden="true">02</span><div><h3 id="import-review-title">Review mapping</h3><p>Nothing changes in the editor until you import the validated draft.</p></div></header>
          {!result && issues.length === 0 && <div className="import-empty"><strong>No validated JSON yet</strong><p>Choose a file or paste JSON, then validate its structure.</p></div>}
          {issues.length > 0 && <div className="import-errors" role="alert"><strong>JSON needs attention</strong><ol>{issues.slice(0, 12).map((issue, index) => <li key={`${index}-${issue}`}>{issue}</li>)}</ol>{issues.length > 12 && <p>Fix the first 12 issues, then validate again.</p>}</div>}
          {result && <>
            <div className="import-ready" role="status"><span aria-hidden="true">✓</span><div><strong>{result.summary.name}</strong><small>{result.summary.locale} · structure accepted</small></div></div>
            <dl className="import-ledger">
              <div><dt>Experience</dt><dd>{result.summary.experience}</dd></div>
              <div><dt>Education</dt><dd>{result.summary.education}</dd></div>
              <div><dt>Skills</dt><dd>{result.summary.skills}</dd></div>
              <div><dt>Projects</dt><dd>{result.summary.projects}</dd></div>
            </dl>
            {result.warnings.length > 0 && <div className="import-warnings"><strong>Review after import</strong><ul>{result.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul><p>These values can be imported, but BEP validation will block saving until they are corrected.</p></div>}
          </>}
        </section>
      </div>

      <section className="document-import-status" aria-labelledby="document-import-title">
        <header><span className="document-import-lamp" aria-hidden="true" /><div><h3 id="document-import-title">Convert DOCX or PDF</h3><p>Creates a separate Generic BEP document. The source passes security admission, byte-level format detection and isolated parsing before any semantic version is committed.</p></div></header>
        <div
          className={documentDragActive ? "document-dropzone is-active" : "document-dropzone"}
          onDragEnter={(event) => { event.preventDefault(); setDocumentDragActive(true); }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => { if (event.currentTarget === event.target) setDocumentDragActive(false); }}
          onDrop={handleDocumentDrop}
        >
          <input id={documentInputID} className="import-file-input" type="file" accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.docx" onChange={handleDocumentFileChange} />
          <label className="button button--quiet" htmlFor={documentInputID}>Choose document</label>
          <span>{documentFile ? `${documentFile.name} · ${formatFileSize(documentFile.size)}` : "or drop one DOCX/PDF here · maximum 25 MiB"}</span>
          <button type="button" className="button button--primary document-convert" onClick={() => void uploadDocument()} disabled={!documentFile || documentBusy}>
            {documentBusy ? "Uploading…" : "Upload and convert"}
          </button>
        </div>
        <div className="document-import-readout" aria-live="polite">
          <span className={`document-state document-state--${documentImportPhase(documentImport, documentStatus, documentBusy, documentIssue)}`} aria-hidden="true" />
          <div>
            <strong>{documentImportTitle(documentImport, documentStatus, documentBusy, documentIssue)}</strong>
            <p>{documentIssue || documentImportDetail(documentImport, documentStatus)}</p>
            {documentImport && <dl>
              <div><dt>Document</dt><dd>{documentImport.document_id}</dd></div>
              <div><dt>Source</dt><dd>{documentImport.binding_id}</dd></div>
            </dl>}
          </div>
        </div>
      </section>
    </div>

    <footer className="import-center-footer">
      <p aria-live="polite">{notice || "Imported JSON opens as a new unsaved draft. Your saved resume stays on the server."}</p>
      <div><button type="button" className="button button--quiet" onClick={onClose}>Keep editing</button><button type="button" className="button button--primary" onClick={applyImport} disabled={!result}>Import new draft</button></div>
    </footer>
  </dialog>;
}

function documentImportTerminal(status: DocumentIngestionStatus): boolean {
  const state = status.source?.sync_state;
  const latest = status.admissions[0];
  return state === "current" || state === "quarantined" || state === "error" || latest?.state === "rejected";
}

function documentImportPhase(record: DocumentImportRecord | null, status: DocumentIngestionStatus | null, busy: boolean, issue: string): string {
  if (issue) return "error";
  if (busy) return "running";
  if (!record) return "idle";
  const state = status?.source?.sync_state;
  const latest = status?.admissions[0];
  if (state === "current") return "complete";
  if (state === "quarantined" || state === "error" || latest?.state === "rejected") return "error";
  return "running";
}

function documentImportTitle(record: DocumentImportRecord | null, status: DocumentIngestionStatus | null, busy: boolean, issue: string): string {
  if (issue) return "Document needs attention";
  if (busy) return "Uploading source bytes";
  if (!record) return "No document selected for conversion";
  const state = status?.source?.sync_state;
  const latest = status?.admissions[0];
  if (state === "current") return "Generic BEP document committed";
  if (state === "quarantined") return "Document quarantined";
  if (state === "error" || latest?.state === "rejected") return "Document rejected";
  if (latest?.state === "scanning") return "Security scan in progress";
  if (latest?.state === "accepted") return "Parsing and semantic mapping";
  return "Queued for security admission";
}

function documentImportDetail(record: DocumentImportRecord | null, status: DocumentIngestionStatus | null): string {
  if (!record) return "The uploaded file stays separate from your current resume until conversion completes.";
  const state = status?.source?.sync_state;
  const latest = status?.admissions[0];
  if (state === "current") return `${record.filename} was converted through the authoritative BEP version path.`;
  if (state === "quarantined") return latest?.error_code ? `Admission stopped: ${latest.error_code}.` : "Admission stopped before parsing.";
  if (state === "error" || latest?.state === "rejected") return latest?.error_code ? `Conversion stopped: ${latest.error_code}.` : "Conversion stopped before commit.";
  if (latest?.state === "accepted") return "The file passed admission. The parser worker is extracting bounded source blocks.";
  if (latest?.state === "scanning") return "The source is being checked before any parser receives its bytes.";
  return `${record.filename} is stored in temporary quarantine and waiting for admission.`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}
